import React, { useEffect, useRef, useState } from 'react';
import { IconChevronDown, IconCheck } from '../icons';
import { ROVING_ITEM_ATTR, onRovingKeyDown } from './rovingFocus';

// Trigger look per variant. `surface`/`outline` are compact toolbar selects;
// `field` matches Drawer's inputClass so it sits in a form next to text inputs.
const TRIGGER_CLASS = {
  surface: 'h-8 px-2 text-xs tracking-[0.4px] bg-surface text-ink',
  outline: 'h-8 px-2 text-xs tracking-[0.4px] bg-card border border-line text-ink',
  field: 'w-full p-3 text-sm bg-card border border-line text-ink focus:outline-none focus:border-brand-500 transition-colors disabled:bg-surface disabled:cursor-not-allowed',
};

// `options` is [{ value, label }] — each list screen passes its own set.
const SortSelect = ({ options, value, onChange, width = 150, variant = 'surface', id, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const isField = variant === 'field';

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // On open, put focus on the selected option so arrows start from it
  useEffect(() => {
    if (!open || !listRef.current) return;
    const items = listRef.current.querySelectorAll(`[${ROVING_ITEM_ATTR}]`);
    const active = Array.from(items).find((el) => el.getAttribute('aria-selected') === 'true');
    (active || items[0])?.focus();
  }, [open]);

  const current = options.find((o) => o.value === value) || options[0];

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleTriggerKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleListKeyDown = (e) => {
    if (e.key === 'Escape') {
      // Stop it reaching useFocusTrap's document listener: Escape closes this list, not the Drawer
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    // Tab leaves the list — close it so it doesn't hang open behind the focus
    if (e.key === 'Tab') {
      setOpen(false);
      return;
    }
    onRovingKeyDown(e);
  };

  return (
    <div ref={ref} className={`relative ${isField ? 'w-full' : 'shrink-0'}`}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleTriggerKeyDown}
        style={isField ? undefined : { width }}
        className={`flex items-center gap-1.5 rounded-lg whitespace-nowrap justify-between ${TRIGGER_CLASS[variant]}`}
      >
        <span className="truncate">{current?.label}</span>
        <IconChevronDown
          size={isField ? 16 : 14}
          className={`text-ink-muted shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          ref={listRef}
          role="listbox"
          aria-labelledby={id}
          onKeyDown={handleListKeyDown}
          className={`absolute z-50 bg-card rounded-xl px-4 py-2.5 shadow-dropdown flex flex-col gap-0.5 max-h-80 overflow-y-auto ${
            isField ? 'left-0 right-0 top-[calc(100%+4px)]' : 'right-0 top-[34px] w-[200px]'
          }`}
        >
          {options.map((opt) => {
            const active = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                {...{ [ROVING_ITEM_ATTR]: '' }}
                onClick={() => { onChange(opt.value); close(); }}
                className={`w-full flex items-center justify-between gap-2 rounded-md font-medium whitespace-nowrap text-ink hover:bg-surface focus:bg-surface focus:outline-none transition-colors ${
                  isField ? 'py-2 text-sm' : 'py-1.5 text-xs'
                }`}
              >
                <span>{opt.label}</span>
                {active && <IconCheck size={16} className="text-ink shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SortSelect;
